import React, { useState } from "react";
import "./GenerateDraftSidebar.css";

export enum GenerationSection {
  DOCUMENT_HEADER = "document-header",
  PATIENT_INFORMATION = "patient-information",
  DIAGNOSTIC_INFORMATION = "diagnostic-information",
  HISTORY_OF_SERVICES = "history-of-services",
  RELEVANT_HISTORY = "relevant-history",
  DIRECT_AND_INDIRECT_MEASURES = "direct-and-indirect-measures",
  INITIAL_OBSERVATION = "initial-observation",
  LANGUAGE_DOMAIN = "language-domain",
  SOCIAL_AND_PLAY_DOMAIN = "social-and-play-domain",
  ATTENDING_SKILLS_DOMAIN = "attending-skills-domain",
  DAILY_LIVING_DOMAIN = "daily-living-domain",
  SAFETY_SKILLS_DOMAIN = "safety-skills-domain",
  FUNCTIONAL_BEHAVIOR_ASSESSMENT = "functional-behavior-assessment",
  CLINICAL_RECOMMENDATIONS = "clinical-recommendations",
  LANGUAGE_GOALS = "language-goals",
  SOCIAL_AND_PLAY_GOALS = "social-and-play-goals",
  ATTENDING_GOALS = "attending-goals",
  DAILY_LIVING_GOALS = "daily-living-goals",
  SAFETY_GOALS = "safety-goals",
  CAREGIVER_GOALS = "caregiver-goals",
  PLAN_FOR_GENERALIZATION = "plan-for-generalization",
  TRANSITION_PLANNING = "transition-planning",
  CRITERIA_FOR_DISCHARGE = "criteria-for-discharge",
  COORDINATION_OF_CARE = "coordination-of-care",
  CRISIS_MANAGEMENT_PLANNING = "crisis-management-planning",
  REQUESTED_HOURS_OF_SERVICE = "requested-hours-of-service",
}

interface SectionGroup {
  name: string;
  sections: {
    id: GenerationSection;
    name: string;
    icon?: string;
  }[];
}

const sectionGroups: SectionGroup[] = [
  {
    name: "Patient Data",
    sections: [
      {
        id: GenerationSection.DOCUMENT_HEADER,
        name: "Document Header",
        icon: "📋",
      },
      {
        id: GenerationSection.PATIENT_INFORMATION,
        name: "Patient Information",
        icon: "👤",
      },
      {
        id: GenerationSection.DIAGNOSTIC_INFORMATION,
        name: "Diagnostic Information",
        icon: "🏥",
      },
      {
        id: GenerationSection.HISTORY_OF_SERVICES,
        name: "History of Services",
        icon: "📋",
      },
      {
        id: GenerationSection.RELEVANT_HISTORY,
        name: "Relevant History",
        icon: "📝",
      },
    ],
  },
  {
    name: "",
    sections: [
      {
        id: GenerationSection.DIRECT_AND_INDIRECT_MEASURES,
        name: "Direct and Indirect Measures",
        icon: "📊",
      },
      {
        id: GenerationSection.INITIAL_OBSERVATION,
        name: "Initial Observation",
        icon: "👁️",
      },
    ],
  },
  {
    name: "Domain Analysis",
    sections: [
      { id: GenerationSection.LANGUAGE_DOMAIN, name: "Language Domain" },
      {
        id: GenerationSection.SOCIAL_AND_PLAY_DOMAIN,
        name: "Social & Play Domain",
      },
      {
        id: GenerationSection.ATTENDING_SKILLS_DOMAIN,
        name: "Attending Skills Domain",
      },
      {
        id: GenerationSection.DAILY_LIVING_DOMAIN,
        name: "Daily Living Domain",
      },
      {
        id: GenerationSection.SAFETY_SKILLS_DOMAIN,
        name: "Safety Skills Domain",
      },
    ],
  },
  {
    name: "",
    sections: [
      {
        id: GenerationSection.FUNCTIONAL_BEHAVIOR_ASSESSMENT,
        name: "Functional Behavior Assessment",
        icon: "📈",
      },
      {
        id: GenerationSection.CLINICAL_RECOMMENDATIONS,
        name: "Clinical Recommendations",
        icon: "💡",
      },
    ],
  },
  {
    name: "Treatment Goals",
    sections: [
      { id: GenerationSection.LANGUAGE_GOALS, name: "Language Goals" },
      {
        id: GenerationSection.SOCIAL_AND_PLAY_GOALS,
        name: "Social & Play Goals",
      },
      { id: GenerationSection.ATTENDING_GOALS, name: "Attending Goals" },
      { id: GenerationSection.DAILY_LIVING_GOALS, name: "Daily Living Goals" },
      { id: GenerationSection.SAFETY_GOALS, name: "Safety Goals" },
      { id: GenerationSection.CAREGIVER_GOALS, name: "Caregiver Goals" },
    ],
  },
  {
    name: "Support Planning",
    sections: [
      {
        id: GenerationSection.PLAN_FOR_GENERALIZATION,
        name: "Plan for Generalization",
      },
      {
        id: GenerationSection.TRANSITION_PLANNING,
        name: "Transition Planning",
      },
      {
        id: GenerationSection.CRITERIA_FOR_DISCHARGE,
        name: "Criteria for Discharge",
      },
      {
        id: GenerationSection.COORDINATION_OF_CARE,
        name: "Coordination of Care",
      },
      {
        id: GenerationSection.CRISIS_MANAGEMENT_PLANNING,
        name: "Crisis Management Planning",
      },
    ],
  },
  {
    name: "",
    sections: [
      {
        id: GenerationSection.REQUESTED_HOURS_OF_SERVICE,
        name: "Requested Hours of Service",
        icon: "📄",
      },
    ],
  },
];

interface GenerateDraftSidebarProps {
  activeSection: GenerationSection;
  onSectionChange: (section: GenerationSection) => void;
  generatedSections: Set<GenerationSection>;
  onPreviewAll?: () => void;
  onClearAll?: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

const GenerateDraftSidebar: React.FC<GenerateDraftSidebarProps> = ({
  activeSection,
  onSectionChange,
  generatedSections,
  onPreviewAll,
  onClearAll,
  isCollapsed = false,
  onToggleCollapse,
}) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    new Set(sectionGroups.filter((g) => g.name).map((g) => g.name))
  );

  const toggleGroup = (groupName: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupName)) {
      newExpanded.delete(groupName);
    } else {
      newExpanded.add(groupName);
    }
    setExpandedGroups(newExpanded);
  };

  return (
    <div className={`generate-sidebar ${isCollapsed ? "collapsed" : ""}`}>
      <div className="generate-sidebar__header">
        {!isCollapsed && (
          <div className="generate-sidebar__title">
            <h2>Treatment Plan Sections</h2>
          </div>
        )}
        {onToggleCollapse && (
          <button
            className="generate-sidebar__toggle"
            onClick={onToggleCollapse}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? "→" : "←"}
          </button>
        )}
      </div>

      {!isCollapsed && (
        <>
          <div className="generate-sidebar__sections">
            {sectionGroups.map((group, groupIndex) => (
              <div key={groupIndex} className="section-group">
                {group.name && (
                  <div
                    className="section-group__header"
                    onClick={() => toggleGroup(group.name)}
                  >
                    <span className="section-group__toggle">
                      {expandedGroups.has(group.name) ? "▼" : "▶"}
                    </span>
                    <span className="section-group__name">{group.name}</span>
                  </div>
                )}
                <div
                  className={`section-group__items ${
                    group.name && !expandedGroups.has(group.name)
                      ? "collapsed"
                      : ""
                  }`}
                >
                  {group.sections.map((section) => (
                    <button
                      key={section.id}
                      className={`section-item ${
                        activeSection === section.id ? "active" : ""
                      } ${
                        generatedSections.has(section.id) ? "generated" : ""
                      }`}
                      onClick={() => onSectionChange(section.id)}
                    >
                      <span className="section-item__icon">
                        {section.icon ||
                          (generatedSections.has(section.id) ? "✓" : "○")}
                      </span>
                      <span className="section-item__name">{section.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="generate-sidebar__footer">
            <div className="footer-buttons">
              <button
                className="btn-preview-all"
                onClick={onPreviewAll}
                disabled={generatedSections.size === 0}
              >
                <span>📄</span> Preview ({generatedSections.size})
              </button>
              <button
                className="btn-clear-all"
                onClick={onClearAll}
                disabled={generatedSections.size === 0}
              >
                <span>🗑️</span> Clear All
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GenerateDraftSidebar;
